'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { AddAddressToAddressBook, addAddressToAddressBookSchema, useUpFetch } from '@repo/sdk'
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Spinner,
  useAddressBookContext,
} from '@repo/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

export const AddNewAddressForm = () => {
  const { companyId, userId } = useAddressBookContext()
  const queryClient = useQueryClient()
  const upFetch = useUpFetch()

  const [open, setOpen] = useState(false)

  const form = useForm<AddAddressToAddressBook>({
    resolver: zodResolver(addAddressToAddressBookSchema),
  })
  const { control, handleSubmit } = form

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: AddAddressToAddressBook) => {
      await upFetch<{ data: AddAddressToAddressBook }>(`companies/${companyId}/address-book`, {
        body: data,
        method: 'POST',
      })

      setOpen(false)
      await queryClient.invalidateQueries({
        queryKey: ['address_book'],
      })
    },
  })

  const onSubmit = async (data: AddAddressToAddressBook) => {
    mutate({ ...data, user_id: userId })
  }

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetTrigger asChild>
        <Button variant={'secondary'}>+ Add new</Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Contacts</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form
            className={'flex h-full flex-col justify-between py-6'}
            onSubmit={e => {
              e.preventDefault()
              e.stopPropagation()
              handleSubmit(onSubmit)(e)
            }}
          >
            <div className={'flex flex-col gap-4'}>
              <FormField
                control={control}
                name={'name'}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={'dark:text-popover-foreground'}>Company name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={'Name'} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={'street'}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={'dark:text-popover-foreground'}>Street name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={'Street'} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={'city'}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={'dark:text-popover-foreground'}>City</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={'City'} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={'post_code'}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={'dark:text-popover-foreground'}>Post code</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={'Post code'} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <SheetFooter>
              <Button
                className={'w-full'}
                onClick={() => setOpen(false)}
                type={'button'}
                variant={'ghost'}
              >
                Cancel
              </Button>
              <Button className={'w-full'} disabled={isPending} type={'submit'}>
                Save {isPending && <Spinner />}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
